import { DollarSign, User, Radar, type LucideIcon } from 'lucide-react';

export const DEMO_DOCUMENT_VERSIONS = [
  { id: 'v5', label: 'v5 (Current)', date: 'Mar 19, 2026', author: 'Kecia Tatum', isCurrent: true },
  { id: 'v4', label: 'v4', date: 'Mar 12, 2026', author: 'Legal Team', isCurrent: false },
  { id: 'v3', label: 'v3', date: 'Feb 28, 2026', author: 'Kecia Tatum', isCurrent: false },
  { id: 'v2', label: 'v2', date: 'Feb 10, 2026', author: 'Nicole Billings', isCurrent: false },
  { id: 'v1', label: 'v1 (Original)', date: 'Jan 15, 2026', author: 'You', isCurrent: false },
];

export const DEMO_DEAL_SUMMARY = {
  description: 'Comprehensive recording agreement for Coco Jones covering recording, touring, merchandising, and publishing. Includes advance payments, tiered royalty splits, and worldwide territory rights with Nasty C JV carve-out.',
  startDate: 'Jan 15, 2026',
  targetClose: 'Apr 1, 2026',
  dealValue: '$2,500,000',
  dealType: 'Recording Deal',
  territory: 'Worldwide (excl. Nasty C JV)',
  duration: '3 albums + 2 options',
  daysElapsed: 63,
  totalDays: 76,
  progressPercent: 65,
  albumCount: 3,
  royaltySplit: '18% base',
  advancePercent: 33,
};

export const DEAL_BREAKDOWN = [
  { label: 'Advance', percent: 33, value: '$833,333', color: 'bg-violet-500' },
  { label: 'Royalties (18%)', percent: 35, value: '$875,000', color: 'bg-emerald-500' },
  { label: 'Touring (20%)', percent: 20, value: '$500,000', color: 'bg-amber-500' },
  { label: 'Merch (25%)', percent: 12, value: '$291,667', color: 'bg-red-500' },
];

export type KeyTermId = 'term-1' | 'term-2' | 'term-3';
export type KeyTermChartKind = 'advance_progress' | 'ownership_split' | 'risk_horizon' | 'holistic_analysis';

export interface KeyDealTerm {
  id: KeyTermId;
  type: 'money' | 'ownership' | 'risk' | 'analysis';
  title: string;
  value: string;
  detail: string;
  icon: LucideIcon;
}

type AdvanceProgressChartData = {
  kind: 'advance_progress';
  progressPercent: number;
  totalAdvance: number;
  installmentCount: number;
};

type OwnershipSplitSegment = {
  name: string;
  value: number;
  color: string;
};

type OwnershipSplitChartData = {
  kind: 'ownership_split';
  segments: OwnershipSplitSegment[];
};

type RiskHorizonPoint = {
  label: string;
  years: number;
  color: string;
};

type RiskHorizonChartData = {
  kind: 'risk_horizon';
  points: RiskHorizonPoint[];
};

type HolisticAnalysisAxis = {
  subject: string;
  score: number;
  fullMark: number;
};

type HolisticAnalysisChartData = {
  kind: 'holistic_analysis';
  axes: HolisticAnalysisAxis[];
};

export type KeyTermChartData =
  | AdvanceProgressChartData
  | OwnershipSplitChartData
  | RiskHorizonChartData
  | HolisticAnalysisChartData;

export const KEY_DEAL_TERMS: KeyDealTerm[] = [
  { id: 'term-1', type: 'money', title: 'Advance Payment', value: '$2,500,000', detail: 'Paid in 3 installments tied to album delivery', icon: DollarSign },
  { id: 'term-2', type: 'ownership', title: 'Master Ownership', value: 'Revenue Split', detail: '18% recording / 20% touring / 25% merch', icon: User },
  { id: 'term-3', type: 'analysis', title: 'Holistic Analysis', value: 'Strong', detail: '5-point advisory assessment', icon: Radar },
];

export const KEY_TERM_CHART_DATA: Partial<Record<KeyTermId, KeyTermChartData>> = {
  'term-1': {
    kind: 'advance_progress',
    progressPercent: 33,
    totalAdvance: 2500000,
    installmentCount: 3,
  },
  'term-2': {
    kind: 'ownership_split',
    segments: [
      { name: 'Recording (18%)', value: 18, color: '#6d7dff' },
      { name: 'Touring (20%)', value: 20, color: '#34d399' },
      { name: 'Merch (25%)', value: 25, color: '#f97316' },
      { name: 'Artist Retained', value: 37, color: '#94a3b8' },
    ],
  },
  'term-3': {
    kind: 'holistic_analysis',
    axes: [
      { subject: 'Financial', score: 5, fullMark: 5 },
      { subject: 'Legal', score: 4, fullMark: 5 },
      { subject: 'Marketing', score: 4, fullMark: 5 },
      { subject: 'Scheduling', score: 3, fullMark: 5 },
      { subject: 'Operations', score: 4, fullMark: 5 },
    ],
  },
};

export const EXAMPLE_ACTIVITY = [
  { id: 'dj-activity-1', user: 'Kecia Tatum', action: 'commented', content: 'Updated territory provisions — added Nasty C JV carve-out in Section 12.', timestamp: '2 hours ago', avatar: undefined },
  { id: 'dj-activity-2', user: 'You', action: 'uploaded', content: 'Recording Agreement v5 with revised territory terms', timestamp: '5 hours ago', avatar: undefined },
  { id: 'dj-activity-3', user: 'Nicole Billings', action: 'approved', content: 'A&R creative terms for Coco Jones Recording Agreement', timestamp: '1 day ago', avatar: undefined },
  { id: 'dj-activity-4', user: 'Kecia Tatum', action: 'commented', content: 'The royalty structure at 18% base rate is competitive for a recording deal of this scope.', timestamp: '2 days ago', avatar: undefined },
  { id: 'dj-activity-5', user: 'System', action: 'created', content: 'Deal created', timestamp: 'Jan 15, 2026', avatar: undefined },
];
